import {
  getAggregatedCountApi,
  getCountsApi,
  getFiltersApi,
  barGraphDataApi,
  performanceGridDataApi,
  getCohortValues,
  createCohort,
  generateCSVDataApi,
  generateCalibrationCSVDataApi,
  getCSVGenerationStatusApi,
  getReviewCycleStageApi,
  triggerPDFReportGenerationApi,
  getPerformanceReviewApi,
  getReviewsManageFilterDataApi,
  reopenReviewFormApi,
  notifyApi,
  forceSubmitApi,
  getReviewCyclesAndCalibrationViewsApi,
  postReviewRecalibrationSaveApi,
  getCalibrationRecomputeApiV1,
} from 'api/analytics/review';
import { REVIEW_RESULT_VIEW } from 'reducers/reviewResultSlice';
import { PREDICTION_CALIBRATION_VIEW_ID } from 'screens/performanceReview/manageAndInsights/TalentPlanning/dataHooks';

import {
  ENGAGEMENT_ADMIN,
  FEEDBACK_ADMIN,
  GOAL_ADMIN,
  ONE_ON_ONE_ADMIN,
  REVIEW_ADMIN,
} from 'constants/ProjectConstants';

import { GroupByKeys, MetricsCard, MetricsCardScore, PerformanceGridBox, Source } from './reviewHelper';

export const ANALYTICS_COHORT_KEYS = ['engagement', 'goals', 'review', 'oneOnOne', 'feedback'];
export const ANALYTICS_COHORT_ROLES = {
  engagement: ENGAGEMENT_ADMIN,
  goals: GOAL_ADMIN,
  review: REVIEW_ADMIN,
  oneOnOne: ONE_ON_ONE_ADMIN,
  feedback: FEEDBACK_ADMIN,
};

export const getFilters = () =>
  getFiltersApi().then((res) => ({
    ...res.entity,
    // cohorts: res.entity.cohort.map( ch => Cohort.fromJSON(ch)),
    cohorts: res.entity.cohort,
    groupByKeys: res.entity.groupByKeys.map((key) => GroupByKeys.fromJSON(key)),
    sources: res.entity.source.map((src) => Source.fromJSON(src)),
  }));

export const getReviewCyclesAndCalibrationViews = ({ cohortId, isPlanOfAction }) =>
  getReviewCyclesAndCalibrationViewsApi({ cohortId, isPlanOfAction }).then((res) => {
    if (isPlanOfAction) {
      return {
        reviewCycles: (res ?? []).map((rc) => ({
          ...rc,
          uuid: String(rc.id),
          displayName: rc.name,
          reviewType: rc.reviewType,
        })),
      };
    }
    return {
      reviewCycles: (res.entity.reviewCycles ?? []).map((rc) => ({
        ...rc,
        uuid: String(rc.id),
        displayName: rc.name,
      })),
      calibrationViews: (res.entity.calibrationViews ?? []).map((cv) => ({
        ...cv,
        id: cv.id ?? PREDICTION_CALIBRATION_VIEW_ID,
        uuid: String(cv.id ?? PREDICTION_CALIBRATION_VIEW_ID),
        displayName: cv.name,
      })),
    };
  });

/**
 * @deprecated 06-08-2024
 */
export const getCounts = ({ cohort, groupByKeys, source }) =>
  getCountsApi({ cohort, groupByKeys, source }).then((res) => res.entity);

export const getAggregatedCount = ({ cohort, groupByKeys, source }) =>
  getAggregatedCountApi({ cohort, groupByKeys, source }).then((res) =>
    res.entity.map((card) => MetricsCard.fromJSON(card))
  );

export const getBarGraphData = ({ cohort, groupByKeys, source, filter }) =>
  barGraphDataApi({
    cohort,
    groupByKeys,
    source,
    filter,
  }).then((res) =>
    res.entity.map((score) => MetricsCardScore.fromJSON(score)).filter(({ viewType }) => viewType === 'RATING')
  );

export const getPerformanceGridData = ({ cohort, groupByKeys, source, filter, type }) =>
  performanceGridDataApi({
    cohort,
    groupByKeys,
    source,
    filter,
    type,
  }).then((res) => {
    if (!res.entity)
      return {
        chartData: [],
        noRating: {},
        isDisplayPictures: false,
      };
    return PerformanceGridBox.fromJSON(res.entity, type);
  });

export const getCohortKeyValues = (pageType = 'REVIEW') =>
  getCohortValues({
    pageType,
  }).then((res) => {
    const contentObj = {};

    Object.keys(res.entity.content).forEach((key) => {
      if (res.entity.content[key]) {
        if (ANALYTICS_COHORT_KEYS.includes(key)) {
          contentObj[key] = res.entity.content[key].data.map((lo) => {
            if (lo.options) {
              return {
                ...lo,
                options: lo.options.map((optObj) => ({
                  ...optObj,
                  id: optObj.id || optObj.displayName,
                })),
                accessibleRole: ANALYTICS_COHORT_ROLES[key],
              };
            }
            return {
              ...lo,
              id: lo.id || lo.displayName,
            };
          });
        } else {
          contentObj[key] = res.entity.content[key].map((lo) => {
            if (lo.options) {
              return {
                ...lo,
                options: lo.options.map((optObj) => ({
                  ...optObj,
                  id: optObj.id || optObj.displayName,
                })),
              };
            }
            return {
              ...lo,
              id: lo.id || lo.displayName,
            };
          });
        }
      }
    });

    const keys = res.entity.keys.map((k) => ({
      ...k,
      accessibleRole: ANALYTICS_COHORT_ROLES[k.key],
    }));
    return {
      ...res.entity,
      keys,
      content: contentObj,
    };
  });

export const createNewCohort = ({ cohortName, query, baseCohortId, visibilityType }) => {
  const bodyObj = {
    cohortName,
    query,
  };

  if (baseCohortId) bodyObj.baseCohort = { uuid: baseCohortId };

  if (visibilityType) bodyObj.visibilityType = visibilityType;

  return createCohort(bodyObj).then((res) => res.entity);
};

/**
 * @deprecated 06-08-2024
 */
export const downloadCSVData = ({ cohort, groupByKeys, source, filter, orderBy }) => {
  let orderBy1 = null;

  if (orderBy?.columnId) orderBy1 = [orderBy];
  return generateCSVDataApi({ cohort, groupByKeys, source, filter, orderBy: orderBy1 });
};

/**
 * @deprecated 06-08-2024
 */
export const downloadCalibrationCSVData = ({ cohort, groupByKeys, source, filter, orderBy }) => {
  let orderBy1 = null;

  if (orderBy?.columnId) orderBy1 = [orderBy];
  return generateCalibrationCSVDataApi({ cohort, groupByKeys, source, filter, orderBy: orderBy1 });
};

export const getStatusOfRequestIds = async (validDownloadRequests) => {
  const queryParams = validDownloadRequests.map((request) => request.uuid);

  const response = await getCSVGenerationStatusApi(queryParams);
  return response.entity;
};

export const getReviewCycleStage = (reviewCycleId) =>
  getReviewCycleStageApi({
    reviewCycleId,
  }).then((res) => res.entity);

export const triggerPDFReportGeneration = ({
  reviewId,
  employeeId,
  providerId,
  receiverEmail,
  reportView = REVIEW_RESULT_VIEW.QUESTION,
  responseId,
  isDeerUI = false,
  reportViewerPersona,
  employeeEmail,
}) =>
  triggerPDFReportGenerationApi({
    reviewId,
    employeeId,
    providerId,
    responseId,
    receiverEmail,
    reportView,
    isDeerUI,
    reportViewerPersona,
    employeeEmail,
  }).then((res) => res.entity);

export const getPerformanceReviewData = ({ empId }) => getPerformanceReviewApi({ empId }).then((res) => res.entity);

export const getReviewsManageFilterData = ({ cohort, filterApi = true }) =>
  getReviewsManageFilterDataApi({
    cohort,
    filterApi,
  }).then((res) => res.entity);

export const getCalibrationRecomputeV1 = (requestBody) =>
  getCalibrationRecomputeApiV1(requestBody).then((res) => res.entity);

export const postReviewRecalibrationSave = ({ requestBody }) =>
  postReviewRecalibrationSaveApi(requestBody).then((res) => res.entity);

export const reopenReviewForm = ({ receiverEmployeeId, providerEmployeeId, reviewCycleId }) =>
  reopenReviewFormApi({ receiverEmployeeId, providerEmployeeId, reviewCycleId }).then((res) => res.entity);

export const manageForceSubmit = ({ providerEmployeeId, receiverEmployeeId, reviewCycleId, providerType }) =>
  forceSubmitApi({
    providerEmployeeId,
    receiverEmployeeId,
    reviewCycleId,
    providerType,
  }).then((res) => res);

export const reviewNotify = ({ feedbackFormId, receiverEmployeeId, providerEmployeeId, providerType }) =>
  notifyApi({
    feedbackFormId,
    receiverEmployeeId,
    providerEmployeeId,
    providerType,
  }).then((res) => res);
